import { useApolloClient, useQuery } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { useFeatureGate } from '@statsig/react-bindings';
import { createContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { SyncClient } from 'twilio-sync';

import { useTranslate } from '@growtherapy/i18n';

import { logger } from '../datadog/logger';
import { sendLoggingEvents } from '../events';
import { useTrackEvent } from '../segment/segment';
import { Statsig } from '../statsig/StatsigProvider';
import { toast } from '../toasts';

/**
 * Dependencies of the application.
 */
export type AppDependencies = {
  SyncClient: typeof SyncClient;
  Statsig: typeof Statsig;
  logger: typeof logger;
  toast: typeof toast;
  sendLoggingEvents: typeof sendLoggingEvents;
  useNavigate: typeof useNavigate;
  useFeatureGate: typeof useFeatureGate;
  useQuery: typeof useQuery;
  useApolloClient: typeof useApolloClient;
  useAuth0: typeof useAuth0;
  useTrackEvent: typeof useTrackEvent;
  useTranslate: typeof useTranslate;
};

export const DEFAULT_APP_DEPENDENCIES: AppDependencies = {
  SyncClient,
  Statsig,
  logger,
  toast,
  sendLoggingEvents,
  useNavigate,
  useFeatureGate,
  useQuery,
  useApolloClient,
  useAuth0,
  useTrackEvent,
  useTranslate,
};

/**
 * Used primarily in testing to override dependencies, but can also be used to
 * inject dependecies in other scenarios.
 *
 * ℹ️ Contexts will provide their default value if used outside of a
 * corresponding context provider.
 * */
export const AppDependenciesContext = createContext<AppDependencies>(DEFAULT_APP_DEPENDENCIES);

/**
 * @see {@link AppDependenciesContext}
 */
export const AppDependenciesProvider = AppDependenciesContext.Provider;
